import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary, PageLayout, TopBar } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { AppRoute } from 'routing/AppRoute.enum';

import * as Styled from './Dod.styles';
import { DodProps } from './Dod.types';
import { GrafanaBar } from './components/grafanaBar/GrafanaBar';
import { useDodFilters } from './hooks/useDodFilters/useDodFilters';

const TABS: Array<{ route: AppRoute; message: keyof typeof AppMessages }> = [
  { route: AppRoute.dod, message: 'dod.tab.overview' },
  { route: AppRoute.dodQueues, message: 'dod.tab.queues' },
  { route: AppRoute.dodDlq, message: 'dod.tab.dlq' },
  { route: AppRoute.dodRetries, message: 'dod.tab.retries' },
  { route: AppRoute.dodWorker, message: 'dod.tab.worker' },
  { route: AppRoute.dodHistory, message: 'dod.tab.history' },
];

export const Dod = ({ connectionStatus, onRefresh, outletContext, toolbarOptions, details }: DodProps): JSX.Element => {
  const { formatMessage } = useLocale();
  const location = useLocation();
  const filters = useDodFilters();
  const outerTheme = useTheme();

  // The shared theme sets palette.background.paper to the sidebar navy (#071E34). Every MUI
  // surface the monitor uses - the queue table, the TKD drawer, select menus, the x-charts tooltip
  // - paints itself with that colour and comes out white-on-navy. The rest of the app never puts
  // data on a Paper, so the override stays scoped to this page.
  const monitorTheme = useMemo(
    () => createTheme(outerTheme, { palette: { background: { paper: outerTheme.palette.common.white } } }),
    [outerTheme],
  );

  return (
    <ErrorBoundary>
      <ThemeProvider theme={monitorTheme}>
        <Styled.ChartTooltipStyle />
        <PageLayout
          header={formatMessage({ id: AppMessages['dod.title'] })}
          renderContent={() => (
            <>
              {connectionStatus !== 'connected' && (
                <Styled.ReconnectBanner role="status">
                  {formatMessage({
                    id: AppMessages[
                      connectionStatus === 'reconnecting' ? 'dod.connection.lost' : 'dod.connection.offlineMode'
                    ],
                  })}
                </Styled.ReconnectBanner>
              )}

              <Styled.StickyToolbar>
                <TopBar gridTemplateColumns="auto auto auto auto auto auto 1fr auto">
                  {/* The range, refresh and filters live in the query string; the tabs carry it along. */}
                  {TABS.map(({ route, message }) => (
                    <TopBar.Link
                      key={route}
                      linkTo={`${route}${filters.queryString}`}
                      isActive={location.pathname === route}
                    >
                      {formatMessage({ id: AppMessages[message] })}
                    </TopBar.Link>
                  ))}
                  <TopBar.EmptyBarWithLine />
                  <TopBar.ButtonsWrapper>
                    <Styled.ConnectionStatus>
                      <Styled.ConnectionDot $status={connectionStatus} />
                      {formatMessage({ id: AppMessages[`dod.connection.${connectionStatus}`] })}
                    </Styled.ConnectionStatus>
                  </TopBar.ButtonsWrapper>
                </TopBar>

                <GrafanaBar
                  filters={filters}
                  onRefresh={onRefresh}
                  rcpOptions={toolbarOptions.rcp}
                  tkdOptions={toolbarOptions.tkd}
                  statusOptions={toolbarOptions.status}
                />
              </Styled.StickyToolbar>

              <Styled.TabContent>
                <Outlet context={outletContext} />
              </Styled.TabContent>
            </>
          )}
          renderDetails={() => details}
        />
      </ThemeProvider>
    </ErrorBoundary>
  );
};
